import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import TimelineBody from '../timeline/shared/TimelineBody';
import type { IssueListItem, IssueTreeNode, CreateIssueData } from '../../services/issueService';
import { useUpdateIssue, useCreateIssue } from '../../hooks/useIssues';
import type { TimeUnit } from '../timeline/shared/timelineUtils';
import {
  flattenTree,
  EMPTY_FILTERS,
  getTodayOffset,
  calculateTimeRange,
} from '../timeline/shared/timelineUtils';
import toast from 'react-hot-toast';

import type { ProjectIssueType } from '../../hooks/useProjectIssueTypes';

interface IssueTimelineViewProps {
  projectId: string;
  items: IssueListItem[];
  onIssueSelect: (cuidId: string) => void;
  projectIssueTypes?: ProjectIssueType[];  // CHG-052
}

const UNIT_LABELS: Record<TimeUnit, string> = { day: '일', week: '주', month: '월' };

/**
 * Convert flat IssueListItem[] to IssueTreeNode[] hierarchy.
 * parentIssueId references the business key (issueId), so we build
 * parent-child relationships using that key.
 */
function buildTreeFromItems(items: IssueListItem[]): IssueTreeNode[] {
  const nodeMap = new Map<string, IssueTreeNode>();

  for (const item of items) {
    nodeMap.set(item.issueId, {
      issueId: item.issueId,
      title: item.title,
      issueType: item.issueType,
      statusId: item.statusId,
      workflowStatus: item.workflowStatus,
      priority: item.priority,
      startDate: item.startDate,
      dueDate: item.dueDate,
      parentIssueId: item.parentIssueId,
      assignee: item.assignee
        ? { id: item.assignee.id, name: item.assignee.name, avatarUrl: item.assignee.avatarUrl }
        : null,
      children: [],
    });
  }

  const roots: IssueTreeNode[] = [];
  for (const item of items) {
    const node = nodeMap.get(item.issueId)!;
    if (item.parentIssueId && nodeMap.has(item.parentIssueId)) {
      nodeMap.get(item.parentIssueId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

export default function IssueTimelineView({
  projectId,
  items,
  onIssueSelect,
  projectIssueTypes,
}: IssueTimelineViewProps) {
  const [timeUnit, setTimeUnit] = useState<TimeUnit>('day');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showAllTasks, setShowAllTasks] = useState(false);
  const ganttScrollRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const updateIssueMutation = useUpdateIssue(projectId);
  const createIssueMutation = useCreateIssue(projectId);

  // 기본: Epic을 root로 갖는 티켓만 노출. 토글이 켜지면 전체 노출.
  const visibleItems = useMemo(() => {
    if (showAllTasks) return items;
    const byKey = new Map<string, IssueListItem>();
    for (const it of items) byKey.set(it.issueId, it);
    const isEpicType = (t: string) => t === 'EPIC' || t === 'Epic';
    const rootType = (item: IssueListItem): string => {
      let cur = item;
      const seen = new Set<string>();
      while (cur.parentIssueId && !seen.has(cur.issueId)) {
        seen.add(cur.issueId);
        const parent = byKey.get(cur.parentIssueId);
        if (!parent) break;
        cur = parent;
      }
      return cur.issueType;
    };
    return items.filter((it) => isEpicType(rootType(it)));
  }, [items, showAllTasks]);

  // Build tree from filtered items
  const treeData = useMemo(() => buildTreeFromItems(visibleItems), [visibleItems]);

  // Auto-expand top-level nodes only on initial load
  useEffect(() => {
    if (!initializedRef.current && treeData.length > 0) {
      initializedRef.current = true;
      setExpandedIds(new Set(treeData.map((n) => n.issueId)));
    }
  }, [treeData]);

  const flattenedNodes = useMemo(
    () => flattenTree(treeData, expandedIds, EMPTY_FILTERS),
    [treeData, expandedIds],
  );

  const handleToggle = useCallback((issueId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  }, []);

  // Map business key -> cuid id for detail panel & API calls
  const issueIdToCuidMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) {
      map.set(item.issueId, item.id);
    }
    return map;
  }, [items]);

  const handleIssueClick = useCallback(
    (issueId: string) => {
      const cuidId = issueIdToCuidMap.get(issueId);
      if (cuidId) onIssueSelect(cuidId);
    },
    [issueIdToCuidMap, onIssueSelect],
  );

  const handleDateChange = useCallback(
    async (issueId: string, field: 'startDate' | 'dueDate', newDate: string) => {
      const cuidId = issueIdToCuidMap.get(issueId);
      if (!cuidId) return;
      try {
        await updateIssueMutation.mutateAsync({
          issueId: cuidId,
          data: { [field]: newDate },
        });
        toast.success('날짜가 변경되었습니다.');
      } catch {
        toast.error('날짜 변경에 실패했습니다.');
      }
    },
    [issueIdToCuidMap, updateIssueMutation],
  );

  // No-op for move (hierarchy changes not supported in issue list context)
  const handleMoveStart = useCallback(
    (_issueId: string, _issueType: string, _e: React.MouseEvent) => {},
    [],
  );

  const handleCreateIssue = useCallback(
    async (parentIssueId: string | null, issueType: string, title: string) => {
      try {
        const data: CreateIssueData = {
          title,
          issueType,
          priority: 'MEDIUM',
        };
        if (parentIssueId) {
          data.parentIssueId = parentIssueId;
        }
        await createIssueMutation.mutateAsync(data);
        // Auto-expand parent so the new child is visible
        if (parentIssueId) {
          setExpandedIds((prev) => new Set([...prev, parentIssueId]));
        }
        toast.success('작업이 생성되었습니다.');
      } catch {
        toast.error('작업 생성에 실패했습니다.');
      }
    },
    [createIssueMutation],
  );

  const handleScrollToToday = useCallback(() => {
    if (!ganttScrollRef.current) return;
    const range = calculateTimeRange(flattenedNodes, timeUnit);
    const todayPx = getTodayOffset(range, timeUnit);
    const containerWidth = ganttScrollRef.current.clientWidth;
    ganttScrollRef.current.scrollLeft = todayPx - containerWidth / 2;
  }, [flattenedNodes, timeUnit]);

  const handleTimeUnitChange = useCallback(
    (unit: TimeUnit) => {
      setTimeUnit(unit);
      setTimeout(() => {
        if (!ganttScrollRef.current) return;
        const range = calculateTimeRange(flattenedNodes, unit);
        const todayPx = getTodayOffset(range, unit);
        const containerWidth = ganttScrollRef.current.clientWidth;
        ganttScrollRef.current.scrollLeft = todayPx - containerWidth / 2;
      }, 50);
    },
    [flattenedNodes],
  );

  // Initial scroll to today
  useEffect(() => {
    if (flattenedNodes.length > 0) {
      setTimeout(handleScrollToToday, 100);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header: time unit selector + today button */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-500">
            {visibleItems.length}건의 작업
          </span>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <span className="text-sm text-gray-500">모든 작업 보기</span>
            <button
              type="button"
              role="switch"
              aria-checked={showAllTasks}
              onClick={() => setShowAllTasks((v) => !v)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${showAllTasks ? 'bg-primary-600' : 'bg-gray-200'}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${showAllTasks ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-1">
            {(['day', 'week', 'month'] as const).map((unit) => (
              <button
                key={unit}
                onClick={() => handleTimeUnitChange(unit)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  timeUnit === unit
                    ? 'bg-white text-gray-900 shadow-sm font-medium'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {UNIT_LABELS[unit]}
              </button>
            ))}
          </div>
          <button
            onClick={handleScrollToToday}
            className="px-3 py-1.5 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
          >
            오늘
          </button>
        </div>
      </div>
      {/* Tree + Gantt body */}
      <TimelineBody
        flattenedNodes={flattenedNodes}
        timeUnit={timeUnit}
        expandedIds={expandedIds}
        onToggle={handleToggle}
        onIssueClick={handleIssueClick}
        ganttScrollRef={ganttScrollRef}
        onDateChange={handleDateChange}
        onMoveStart={handleMoveStart}
        dropTarget={null}
        onCreateIssue={handleCreateIssue}
        projectIssueTypes={projectIssueTypes}
      />
    </div>
  );
}
